import { useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { useDeleteBookmark, useRestoreBookmark } from './useBookmarks';
import type { BookmarkedActivity } from '../types/bookmark.types';

interface UndoableDeleteOptions {
  onDeleteSuccess?: () => void;
  onRestoreSuccess?: () => void;
  undoTimeoutMs?: number;
}

/**
 * Hook for managing bookmark deletion with undo capability
 * Provides optimistic updates and delayed permanent deletion
 */
export function useUndoableDelete(options: UndoableDeleteOptions = {}) {
  const {
    onDeleteSuccess,
    onRestoreSuccess,
    undoTimeoutMs = 5000, // 5 seconds to undo
  } = options;

  const deleteBookmark = useDeleteBookmark();
  const restoreBookmark = useRestoreBookmark();

  // Track pending deletions that can be undone
  const pendingDeletions = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  /**
   * Delete a bookmark with undo capability
   */
  const deleteWithUndo = useCallback(
    async (bookmark: BookmarkedActivity) => {
      const bookmarkId = bookmark.id;

      // Clear any existing timeout for this bookmark
      const existingTimeout = pendingDeletions.current.get(bookmarkId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      try {
        // Perform soft delete
        await deleteBookmark.mutateAsync(bookmarkId);

        // Show toast with undo button
        toast.success('Activity removed from library', {
          description: bookmark.activity.title,
          duration: undoTimeoutMs,
          action: {
            label: 'Undo',
            onClick: async () => {
              // Clear timeout to prevent permanent deletion
              const timeout = pendingDeletions.current.get(bookmarkId);
              if (timeout) {
                clearTimeout(timeout);
                pendingDeletions.current.delete(bookmarkId);
              }

              try {
                // Restore the bookmark
                await restoreBookmark.mutateAsync(bookmarkId);
                toast.success('Activity restored', {
                  description: bookmark.activity.title,
                  duration: 2000,
                });
                onRestoreSuccess?.();
              } catch (error) {
                console.error('Failed to restore bookmark:', error);
                toast.error('Failed to restore activity', {
                  description: error instanceof Error ? error.message : 'Please try again',
                });
              }
            },
          },
        });

        // Set timeout for permanent deletion (no action needed, already soft deleted)
        const timeout = setTimeout(() => {
          pendingDeletions.current.delete(bookmarkId);
          // Soft delete is already applied, no further action needed
        }, undoTimeoutMs);

        pendingDeletions.current.set(bookmarkId, timeout);
        onDeleteSuccess?.();
      } catch (error) {
        console.error('Failed to delete bookmark:', error);
        toast.error('Failed to remove activity', {
          description: error instanceof Error ? error.message : 'Please try again',
          duration: 4000,
        });
      }
    },
    [
      deleteBookmark,
      restoreBookmark,
      undoTimeoutMs,
      onDeleteSuccess,
      onRestoreSuccess,
    ]
  );

  /**
   * Cancel all pending deletions (cleanup on unmount)
   */
  const cancelAllPending = useCallback(() => {
    pendingDeletions.current.forEach((timeout) => clearTimeout(timeout));
    pendingDeletions.current.clear();
  }, []);

  return {
    deleteWithUndo,
    cancelAllPending,
    isDeleting: deleteBookmark.isPending,
    isRestoring: restoreBookmark.isPending,
  };
}
